import React, { FC, useMemo } from 'react';
import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';
import { cn } from '../../utils/classNames';
import { BookingSlot, ResourceSchema } from '../../types/booking';
import { SLOT_STYLES } from './slotStyles';

interface Props {
  date: DateTime;
  slots: BookingSlot[];
  schema?: ResourceSchema;
  isLoading?: boolean;
  selected?: boolean;
  onSlotSelect?: (slot: BookingSlot) => void;
  onDaySelect?: () => void;
  viewContext?: 'calendar' | 'quickBooking';
}

const DaySkeleton: FC = () => (
  <div className="animate-pulse space-y-2">
    <div className="h-6 bg-gray-200 rounded w-16" />
    <div className="space-y-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="h-10 bg-gray-200 rounded" aria-hidden="true" />
      ))}
    </div>
  </div>
);

export const CalendarDay: FC<Props> = ({
  date,
  slots,
  schema,
  isLoading,
  selected,
  onSlotSelect,
  onDaySelect,
  viewContext = 'calendar',
}) => {
  const now = DateTime.now();
  const isCurrentDate = date.hasSame(now, 'day');
  const isNewMonth = date.day === 1 && now.month !== date.month;

  const slotStyle = useMemo(() => {
    if (viewContext === 'calendar') {
      return 'py-1 w-full [&>span]:opacity-65';
    }

    if (slots.length <= 4) {
      return 'py-9 w-full';
    }
    if (slots.length <= 6) {
      return 'py-7 w-full';
    }
    if (slots.length <= 8) {
      return 'py-6 w-full';
    }
    if (slots.length <= 10) {
      return 'py-4 w-full';
    }
    if (slots.length <= 12) {
      return 'py-3 w-full';
    }
    return 'py-4 w-[calc(50%-2px)]';
  }, [slots.length, viewContext]);

  const content = useMemo(() => {
    if (isLoading) {
      return <DaySkeleton />;
    }

    if (!slots?.length && date.diffNow().milliseconds > 0) {
      return (
        <div className="flex items-center justify-center h-full text-sm text-gray-500">
          <FormattedMessage defaultMessage="Inga tider" />
        </div>
      );
    }

    const isFutureDate = schema?.configuration?.daysInAdvance
      ? date > now.plus({ days: schema.configuration.daysInAdvance - 1 })
      : false;

    if (isFutureDate) {
      return (
        <div className="flex items-center justify-center h-full text-sm text-gray-500">
          <FormattedMessage defaultMessage="Ej bokningsbart än" />
        </div>
      );
    }

    return (
      <div className="h-full flex content-start flex-wrap gap-1 justify-center grow">
        {slots.map((slot) => {
          const startTime = DateTime.fromISO(slot.start).setZone('local');
          const style = SLOT_STYLES[slot.status];

          return (
            <div
              key={slot.id}
              onClick={() => style.cursor === 'pointer' && onSlotSelect?.(slot)}
              className={cn(
                'rounded flex flex-col justify-center items-center transition-all',
                `cursor-${style.cursor}`,
                slotStyle,
              )}
              style={{ background: style.color, color: style.textColor }}
            >
              <span className="text-sm leading-none font-medium text-left">
                {startTime.toFormat('HH:mm')}
              </span>
            </div>
          );
        })}
      </div>
    );
  }, [
    date,
    isLoading,
    now,
    onSlotSelect,
    schema?.configuration.daysInAdvance,
    slotStyle,
    slots,
  ]);

  return (
    <div
      onClick={onDaySelect}
      className={cn(
        'bg-gray-50 p-3 min-h-[180px] flex flex-col gap-2 rounded-xl',
        selected && 'shadow-[0_0_0_4px_#666]',
      )}
    >
      <div
        className={cn(
          'flex items-center rounded-lg px-2 py-1 text-sm',
          isCurrentDate
            ? '!bg-blue-light !text-blue-dark font-medium'
            : 'text-gray-600',
        )}
      >
        <span className="font-medium">{date.day}</span>
        <span className="ml-1 text-md opacity-85">{date.weekdayShort}</span>
        {isNewMonth && (
          <span className="ml-auto text-md">{date.monthShort}</span>
        )}
      </div>

      <div className="flex-1">{content}</div>
    </div>
  );
};
